import { useRef, useState } from 'react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { LivePreviewRail } from '@/pages/PersonaWorkshop/LivePreviewRail'
import { ReviewPanel } from './ReviewPanel'
import { CoachPanel } from './CoachPanel'
import { useComposerStore } from '@/stores/composerStore'
import type { Finding, FindingLocation } from '@/utils/bundleFindings'

interface Props {
  personaId: string
  bundleId: string | null
  hasUnsavedChanges: boolean
  findings: Finding[]
  onJump: (loc: FindingLocation) => void
  avatarUrl?: string | null
  personaName?: string
  defaultTab?: 'coach' | 'preview' | 'review'
}

export function CoachRail({ personaId, bundleId, hasUnsavedChanges, findings, onJump, avatarUrl, personaName, defaultTab = 'preview' }: Props) {
  const [tab, setTab] = useState<string>(defaultTab)
  const [seed, setSeed] = useState<{ text: string; nonce: number } | null>(null)
  const bundle = useComposerStore((s) => s.draftBundle ?? s.serverBundle)

  // Nonce, not just text: CoachPanel dedupes by identity, so re-clicking
  // the same finding must produce a distinct object or nothing is sent.
  //
  // The counter is a ref rather than being derived from `seed`, because
  // `seed` is cleared on consumption below — deriving from it would reset
  // the nonce to 1 after every send, and a CoachPanel that never unmounted
  // would recognise its own already-sent nonce and stay silent.
  const nonceRef = useRef(0)
  const fixWithCoach = (text: string) => {
    nonceRef.current += 1
    setSeed({ text, nonce: nonceRef.current })
    setTab('coach')
  }

  // Drop the seed once the coach has actually sent it. This rail outlives
  // CoachPanel — Radix unmounts inactive TabsContent — so the seed, not the
  // panel's internal ref, is what a returning panel would act on.
  const handleSeedConsumed = () => setSeed(null)

  return (
    <Tabs value={tab} onValueChange={setTab} className="flex h-full flex-col">
      <TabsList>
        <TabsTrigger value="coach">💬 Coach</TabsTrigger>
        <TabsTrigger value="preview">Preview</TabsTrigger>
        <TabsTrigger value="review">Review</TabsTrigger>
      </TabsList>
      <TabsContent value="coach" className="min-h-0 flex-1 overflow-y-auto">
        <CoachPanel
          personaId={personaId}
          personaName={personaName ?? ''}
          avatarUrl={avatarUrl}
          bundle={bundle}
          findings={findings}
          seededRequest={seed}
          onSeedConsumed={handleSeedConsumed}
        />
      </TabsContent>
      <TabsContent value="preview" className="min-h-0 flex-1 overflow-y-auto">
        <LivePreviewRail
          personaId={personaId}
          bundleId={bundleId}
          hasUnsavedChanges={hasUnsavedChanges}
          findings={findings}
          onJump={onJump}
          avatarUrl={avatarUrl}
          personaName={personaName}
        />
      </TabsContent>
      <TabsContent value="review" className="min-h-0 flex-1 overflow-y-auto">
        <ReviewPanel
          personaId={personaId}
          wiringFindings={findings}
          onJump={onJump}
          onFixWithCoach={fixWithCoach}
        />
      </TabsContent>
    </Tabs>
  )
}
